import React, { useState } from 'react';
import {
  FormattedMessage, useIntl,
} from 'react-intl';
import {
  Alert,
  Button,
  Heading,
  LinkAsButton,
  RadioGroup,
  Spinner,
} from '@printdeal/ui-library';
import { navigate } from 'gatsby';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';
import useGetMandateIssuersQuery from '../../hooks/mandates/useGetMandateIssuersQuery';
import useCreateMandateMutation from '../../hooks/mandates/useCreateMandateMutation';
import MandateCallbackAlerts from './components/MandateCallbackAlerts';
import MandateIssuerIcon from './components/MandateIssuerIcon';

const CreateMandatesPage = () => {
  const intl = useIntl();
  const getMandateIssuersQuery = useGetMandateIssuersQuery();
  const createMandateMutation = useCreateMandateMutation();

  const [selectedIssuer, setSelectedIssuer] = useState<string | undefined>();

  return (
    <>
      <Head title={intl.formatMessage({ id: 'account.mandates.page_title' })} />
      <div className="pd-flex pd-flex-col pd-gap-4">
        <Breadcrumb className="pd-mb-4">
          <Breadcrumb.Item to="../..">
            <FormattedMessage id="account.index.title" defaultMessage="My account" />
          </Breadcrumb.Item>
          <Breadcrumb.Item to="..">
            <FormattedMessage id="account.mandates.page_title" defaultMessage="My mandates" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>
            <FormattedMessage id="account.mandates.create.title" defaultMessage="Create" />
          </Breadcrumb.Item>
        </Breadcrumb>

        <Heading as="h2">
          <FormattedMessage id="account.createMandate.title" defaultMessage="Create mandate" />
        </Heading>

        <MandateCallbackAlerts />

        {(getMandateIssuersQuery.isError || (getMandateIssuersQuery.data && !getMandateIssuersQuery.data.length)) && (
          <Alert variant="error">
            <FormattedMessage id="account.mandates.issuerlist.error" defaultMessage="Error getting mandate issuers" />
          </Alert>
        )}

        {getMandateIssuersQuery.isLoading && (
          <div className="pd-flex pd-justify-center pd-p-10">
            <Spinner size="lg" />
          </div>
        )}

        {getMandateIssuersQuery.data && getMandateIssuersQuery.data.length > 0 && (
          <>
            <RadioGroup
              id="mandate-issuer-radio-group"
              name="mandate-issuer-radio-group"
              label={intl.formatMessage({ id: 'account.createMandate.issuer.text', defaultMessage: 'Select your bank' })}
            >
              {getMandateIssuersQuery.data.map((issuer) => (
                <RadioGroup.Field
                  key={issuer.id}
                  id={`mandate-issuer-${issuer.id}`}
                  value={issuer.id}
                  label={issuer.name}
                  disabled={createMandateMutation.isPending}
                  description={<MandateIssuerIcon issuerId={issuer.id} />}
                  onClick={() => setSelectedIssuer(issuer.id)}
                />
              ))}
            </RadioGroup>
            <div className="pd-mb-6">
              <Button
                iconBefore="file"
                isLoading={createMandateMutation.isPending}
                disabled={!selectedIssuer}
                onClick={async () => {
                  if (!selectedIssuer) {
                    return;
                  }

                  const redirectUrl = await createMandateMutation.mutateAsync(selectedIssuer);
                  navigate(redirectUrl);
                }}
              >
                <FormattedMessage id="account.createMandate.submit.button" defaultMessage="Create mandate" />
              </Button>
            </div>
          </>
        )}

        <div>
          <LinkAsButton
            variant="secondary"
            iconBefore="chevron-left"
            onClick={() => navigate('..')}
            className="pd-text-sm"
          >
            <FormattedMessage id="account.linkToMyMandates" defaultMessage="Back to my mandates" />
          </LinkAsButton>
        </div>
      </div>
    </>
  );
};

export default CreateMandatesPage;
